import { useEffect, type RefObject } from 'react';

import { useDragDropManager } from 'react-dnd';

import type { AutoScrollOpts } from './model';

export function useAutoScroll(
  ref: RefObject<HTMLElement>,
  { margin = 60, maxSpeed = 40, horizontal = false }: AutoScrollOpts = {}
) {
  const manager = useDragDropManager();

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    let raf = 0;

    const calcSpeed = (distFromEdge: number) => {
      return Math.min(maxSpeed, ((margin - distFromEdge) / margin) * maxSpeed);
    };

    const getDy = (upDist: number, downDist: number) => {
      if (upDist < margin) {
        return -calcSpeed(upDist);
      } else if (downDist < margin) {
        return calcSpeed(downDist);
      } else {
        return 0;
      }
    };

    const getDx = (leftDist: number, rightDist: number) => {
      if (!horizontal) {
        return 0;
      } else if (leftDist < margin) {
        return -calcSpeed(leftDist);
      } else if (rightDist < margin) {
        return calcSpeed(rightDist);
      } else {
        return 0;
      }
    };

    const loop = () => {
      const monitor = manager.getMonitor();
      if (!monitor.isDragging()) {
        raf = requestAnimationFrame(loop);
        return undefined; // explicit return
      }

      const point = monitor.getClientOffset();
      const el = ref.current;
      if (point && el) {
        const { top, bottom, left, right } = el.getBoundingClientRect();

        const dy = getDy(point.y - top, bottom - point.y);
        const dx = getDx(point.x - left, right - point.x);

        if (dx || dy) {
          el.scrollBy(dx, dy);
        }
      }

      raf = requestAnimationFrame(loop);
      return undefined; // explicit
    };

    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [manager, ref, margin, maxSpeed, horizontal]);
}

export default useAutoScroll;
